import {isEqual} from 'underscore';

import {AnalysisGraphQL, AnalysisViewLineageNode} from 'analysis-tool/AnalysisTypes';
import {
  assertViewExists,
  getEditViewRightSidebarItemForLineageNode,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {draftStepGlobalState} from 'analysis-tool/right-sidebar/components/useMaybeDraftStep';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {assert} from 'Asserts';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * @package
 */
export const selectedRightSidebarItemGlobalState = makeUseGlobalClientState<AnalysisRightSidebarItem | null>(
  null
);

/**
 * Return the next `AnalysisRightSidebarItem` after deleting steps when the current item is a `EDIT_VIEW`.
 *
 * When the selected right sidebar item is a `EDIT_VIEW`, and we delete a step, that sidebar item might become
 * invalid. We use the following logic to determine the next right sidebar item:
 *  - If the step referenced by the right sidebar item isn't being deleted, we don't need to update the right
 * sidebar item, so we return it.
 *  - If the step referenced by the right sidebar item is being deleted, we select the last remaining step in
 *  the view.
 *  - If the entire view is being deleted, we return `null` to close the right sidebar.
 *
 * @returns The next right sidebar `AnalysisRightSidebarItem` after deleting steps when the current item is a
 * `EDIT_VIEW`.
 * @package
 */
export function getNextRightSidebarItemForView(
  analysis: AnalysisGraphQL,
  selectedRightSidebarItem: AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW>,
  stepsToDelete: Set<string>
): AnalysisRightSidebarItem | null {
  const view = getViewForViewReference(analysis, selectedRightSidebarItem.viewReference);
  assertViewExists(view, analysis, selectedRightSidebarItem.viewReference);
  assert(view.lineage.length > 0, 'Expected view lineage to not be empty');

  const remainingLineageNodes = view.lineage.filter(
    ({step}: AnalysisViewLineageNode) => !stepsToDelete.has(step.id)
  );

  // If the selected item isn't being deleted, we don't need to update the selected item, so return null
  if (remainingLineageNodes.some(({step}) => step.id === selectedRightSidebarItem.viewReference.stepId)) {
    return selectedRightSidebarItem;
    // Otherwise, select the last remaining node in the view
  } else if (remainingLineageNodes.length > 0) {
    const lastRemainingNode = remainingLineageNodes[remainingLineageNodes.length - 1];
    return getEditViewRightSidebarItemForLineageNode(lastRemainingNode);
  }
  return null;
}

/**
 * Return the next `AnalysisRightSidebarItem` after deleting steps when the current item is a `EDIT_STEP`.
 *
 * If the step referenced by the right sidebar item is being deleted, return `null` to close the right sidebar.
 * Otherwise, we return the selected item.
 *
 * @returns The next `AnalysisRightSidebarItem` after deleting steps when the current item is a `EDIT_STEP`.
 * @package
 */
export function getNextRightSidebarItemForStep(
  selectedRightSidebarItem: AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_STEP>,
  stepsToDelete: Set<string>
): AnalysisRightSidebarItem | null {
  if (stepsToDelete.has(selectedRightSidebarItem.stepId)) {
    return null;
  }

  return selectedRightSidebarItem;
}

/**
 * Return the `AnalysisRightSidebarItem` that should be selected after the delete operations.
 *
 * The selected right sidebar item might reference a step that is being deleted, so it will become invalid
 * after the delete operation.
 *
 * @returns The next selected `AnalysisRightSidebarItem` after deleting steps.
 * @package
 */
export function getNextRightSidebarItem(
  analysis: AnalysisGraphQL,
  selectedRightSidebarItem: AnalysisRightSidebarItem | null,
  stepsToDelete: Set<string>
): AnalysisRightSidebarItem | null {
  if (selectedRightSidebarItem == null) {
    return null;
  } else if (selectedRightSidebarItem.type === AnalysisRightSidebarItemType.EDIT_VIEW) {
    return getNextRightSidebarItemForView(analysis, selectedRightSidebarItem, stepsToDelete);
  } else if (selectedRightSidebarItem.type === AnalysisRightSidebarItemType.EDIT_STEP) {
    return getNextRightSidebarItemForStep(selectedRightSidebarItem, stepsToDelete);
  }

  return selectedRightSidebarItem;
}

/**
 * Updates selected right sidebar item after deleting analysis steps to maintain valid state.
 *
 * - This function reads the selected `AnalysisRightSidebarItem` from the client, and then it checks if it
 * needs to be updated after the delete operation.
 * - If it needs to be updated, it updates the client state and stores the previous values.
 * - If it doesn't need to be updated, it doesn't update the client state and doesn't store the previous values.
 * - If the backend deletion call fails, it rolls back the client state to the previous values if it was updated.
 */
export async function updateSelectedRightSidebarItemForDelete(
  deleteFunction: () => Promise<void>,
  analysis: AnalysisGraphQL,
  clientStateId: string,
  stepsToDelete: Set<string>
): Promise<void> {
  const selectedRightSidebarItem = selectedRightSidebarItemGlobalState.getState(clientStateId);
  let previousSelectedRightSidebarItem: AnalysisRightSidebarItem | null | undefined;

  const nextSelectedRightSidebarItem = getNextRightSidebarItem(
    analysis,
    selectedRightSidebarItem,
    stepsToDelete
  );
  if (!isEqual(selectedRightSidebarItem, nextSelectedRightSidebarItem)) {
    selectedRightSidebarItemGlobalState.updateState(clientStateId, nextSelectedRightSidebarItem);
    previousSelectedRightSidebarItem = selectedRightSidebarItem;
  }

  try {
    await deleteFunction();
  } catch (error) {
    const selectedRightSidebarItem = selectedRightSidebarItemGlobalState.getState(clientStateId);
    if (
      previousSelectedRightSidebarItem !== undefined &&
      !isEqual(selectedRightSidebarItem, previousSelectedRightSidebarItem)
    ) {
      selectedRightSidebarItemGlobalState.updateState(clientStateId, previousSelectedRightSidebarItem);
    }
    throw error;
  }
}

export default function useRightSidebarItem(clientStateId: string): {
  selectedRightSidebarItem: AnalysisRightSidebarItem | null;
  setSelectedRightSidebarItem: (item: AnalysisRightSidebarItem | null) => void;
  clearRightSidebarItem: () => void;
} {
  const [selectedRightSidebarItem, setSelectedRightSidebarItem] =
    selectedRightSidebarItemGlobalState.useGlobalClientState(clientStateId);

  return {
    selectedRightSidebarItem,
    setSelectedRightSidebarItem,
    clearRightSidebarItem: () => {
      if (
        selectedRightSidebarItem &&
        selectedRightSidebarItem.type === AnalysisRightSidebarItemType.EDIT_VIEW
      ) {
        draftStepGlobalState.deleteState(selectedRightSidebarItem.viewReference.stepId);
      }
      setSelectedRightSidebarItem(null);
    },
  };
}
